export type DelayUnit = "minutes" | "hours" | "days";

export type StepDelay = {
  minutes?: number;
  hours?: number;
  days?: number;
};

export const DELAY_UNITS: Array<{ value: DelayUnit; label: string }> = [
  { value: "minutes", label: "Minutos" },
  { value: "hours", label: "Horas" },
  { value: "days", label: "Días" },
];

export const getDelayParts = (
  delay: StepDelay | undefined
): { amount: number; unit: DelayUnit } => {
  if (delay?.minutes && delay.minutes > 0) {
    return { amount: delay.minutes, unit: "minutes" };
  }
  if (delay?.hours && delay.hours > 0) {
    return { amount: delay.hours, unit: "hours" };
  }
  return { amount: delay?.days ?? 1, unit: "days" };
};

export const buildDelay = (amount: number, unit: DelayUnit): StepDelay => {
  const safeAmount = Math.max(1, Math.floor(Number(amount) || 1));
  if (unit === "minutes") return { minutes: safeAmount };
  if (unit === "hours") return { hours: safeAmount };
  return { days: safeAmount };
};

export const delayToMinutes = (delay: StepDelay | undefined): number => {
  if (!delay) return 0;
  return (
    (delay.minutes || 0) +
    (delay.hours || 0) * 60 +
    (delay.days || 0) * 24 * 60
  );
};

export const formatDelayLabel = (delay: StepDelay | undefined): string => {
  const { amount, unit } = getDelayParts(delay);
  if (unit === "minutes") {
    return amount === 1 ? "1 minuto" : `${amount} minutos`;
  }
  if (unit === "hours") {
    return amount === 1 ? "1 hora" : `${amount} horas`;
  }
  return amount === 1 ? "1 día" : `${amount} días`;
};

export const formatTimelineOffset = (totalMinutes: number): string => {
  if (totalMinutes <= 0) return "Inicio";

  const days = Math.floor(totalMinutes / (24 * 60));
  const hours = Math.floor((totalMinutes % (24 * 60)) / 60);
  const minutes = totalMinutes % 60;

  if (days > 0 && hours === 0 && minutes === 0) {
    return days === 1 ? "Día 1" : `Día ${days}`;
  }
  if (days > 0) {
    const parts = [`Día ${days}`];
    if (hours) parts.push(`${hours} h`);
    if (minutes) parts.push(`${minutes} min`);
    return parts.join(" · ");
  }
  if (hours > 0 && minutes === 0) {
    return hours === 1 ? "1 hora" : `${hours} h`;
  }
  if (hours > 0) {
    return `${hours} h ${minutes} min`;
  }
  return minutes === 1 ? "1 min" : `${minutes} min`;
};
